import { BorderOutlined, CheckSquareFilled, UserAddOutlined } from '@ant-design/icons';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { PopupTransition } from 'components/@extended/Transitions';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { useField } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { Column, Row } from 'react-table';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import MultiTableDialogSelectorProps from './dto';

const MultiTableDialogSelector = <T extends {}>({
    name,
    buttonLabel,
    dialogTitle,
    defaultVisibleColumnConfig,
    defaultTableColumnConfig,
    searchableTableColumnConfig,
    fetchDataCb,
    idAccessor = 'id',
}: MultiTableDialogSelectorProps<T>) => {
    const [field, meta, helpers] = useField(name);

    const [openDialog, setOpenDialog] = useState(false);
    const [dataList, setDataList] = useState<Array<T>>([]);
    const [pageCount, setPageCount] = useState(0);
    const [selectedItems, setSelectedItems] = useState<Array<any>>([]);

    const isItemSelected = useCallback(
        (item: any) => {
            const itemId = item[idAccessor];
            return selectedItems.some((selected) => selected[idAccessor] === itemId);
        },
        [selectedItems, idAccessor],
    );

    const toggleItemSelection = useCallback(
        (item: any) => {
            setSelectedItems((prevItems) => {
                const isSelected = prevItems.some((selected) => selected[idAccessor] === item[idAccessor]);
                return isSelected
                    ? prevItems.filter((selected) => selected[idAccessor] !== item[idAccessor])
                    : [...prevItems, item];
            });
        },
        [idAccessor],
    );

    const renderCheckboxCell = useCallback(
        (cellProps: { row: Row }) => {
            const item = cellProps.row.original as any;
            const isSelected = isItemSelected(item);
            return isSelected ? (
                <CheckSquareFilled style={{ fontSize: '1.2rem', color: '#1890ff' }} />
            ) : (
                <BorderOutlined style={{ fontSize: '1.2rem', color: '#8c8c8c' }} />
            );
        },
        [isItemSelected],
    );

    const defaultColumnConfig: Column[] = useMemo(
        () => [
            {
                Header: '',
                accessor: 'checkbox' as const,
                disableFilters: true,
                disableSortBy: true,
                Cell: renderCheckboxCell,
            },
            ...defaultTableColumnConfig,
        ],
        [defaultTableColumnConfig, renderCheckboxCell],
    );

    const fetchData: BaseTableFetchFuncProps = async ({ pageSize, pageIndex, filters, searchTerm }) => {
        const data = await fetchDataCb({
            pageIndex,
            pageSize,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, searchableTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setDataList(data.elements);
            setPageCount(Math.ceil(data.total / pageSize));
        }
    };

    const handleOpenDialog = () => {
        setSelectedItems(field.value || []);
        setOpenDialog(true);
    };

    const handleConfirm = () => {
        helpers.setValue(selectedItems);
        setOpenDialog(false);
    };

    const handleCancel = () => {
        setOpenDialog(false);
    };

    return (
        <>
            {meta.touched && meta.error && <Typography color="red">{meta.error}</Typography>}
            <Button variant="outlined" color="primary" onClick={handleOpenDialog} startIcon={<UserAddOutlined />}>
                {buttonLabel}
            </Button>
            <Dialog
                maxWidth="xl"
                TransitionComponent={PopupTransition}
                fullWidth
                open={openDialog}
                sx={{ maxHeight: '100vh', '& .MuiDialog-paper': { p: 0 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2 }}>{dialogTitle ?? 'Select items'}</DialogTitle>
                <DialogContent dividers>
                    <ScrollX>
                        <BaseTable
                            data={dataList}
                            fetchData={fetchData}
                            pageCount={pageCount}
                            defaultColumnConfig={defaultColumnConfig}
                            defaultVisibleColumnConfig={['checkbox', ...defaultVisibleColumnConfig]}
                            onRowClick={toggleItemSelection}
                        />
                    </ScrollX>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" color="error" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleConfirm}>
                            Confirm
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MultiTableDialogSelector;
